import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { companyInfo } from '../data/tourData';
import './CancellationPolicy.css';

const CancellationPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Cancellation Policy - NomadTrailz</title>
        <meta name="description" content="Read our cancellation policy to understand refund terms and conditions for NomadTrailz tours." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="policy-hero">
          <div className="hero-background">
            <img 
              src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
              alt="Mountain Landscape" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Cancellation Policy
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Understanding our refund and cancellation terms
            </motion.p>
          </div>
        </section>

        {/* Cancellation Content */}
        <section className="policy-content">
          <div className="container">
            <motion.div 
              className="content-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="policy-section">
                <h2>1. Cancellation Process</h2>
                <p>
                  All cancellation requests must be submitted in writing via email to {companyInfo.email}. 
                  Cancellations are not confirmed until you receive a written confirmation from our team. 
                  Verbal cancellations are not accepted.
                </p>
              </div>

              <div className="policy-section">
                <h2>2. Refund Policy</h2>
                <p>
                  Refunds are processed according to the following schedule based on the number of days 
                  before the tour departure date:
                </p>
                <ul>
                  <li><strong>More than 30 days:</strong> 90% of the total amount refunded</li>
                  <li><strong>15-30 days:</strong> 50% of the total amount refunded</li>
                  <li><strong>7-14 days:</strong> 25% of the total amount refunded</li>
                  <li><strong>Less than 7 days:</strong> No refund</li>
                </ul>
                <p>
                  Refund percentages are calculated on the total package price excluding non-refundable components.
                </p>
              </div>

              <div className="policy-section">
                <h2>3. Processing Time</h2>
                <p>
                  Refunds are typically processed within 7-14 business days after cancellation confirmation. 
                  The time to receive the refund in your account may vary depending on your bank or payment 
                  method provider.
                </p>
              </div>

              <div className="policy-section">
                <h2>4. Non-Refundable Items</h2>
                <p>
                  The following items are non-refundable regardless of cancellation timing:
                </p>
                <ul>
                  <li>Airfare and transportation tickets</li>
                  <li>Permits and entry fees</li>
                  <li>Travel insurance premiums</li>
                  <li>Processing and administrative fees</li>
                  <li>Third-party service charges</li>
                </ul>
              </div>

              <div className="policy-section">
                <h2>5. Force Majeure</h2>
                <p>
                  In case of force majeure events (natural disasters, political unrest, government 
                  restrictions, etc.), we may offer:
                </p>
                <ul>
                  <li>Full refund of the tour cost</li>
                  <li>Rescheduling to a later date</li>
                  <li>Credit for future tours</li>
                </ul>
                <p>
                  The specific option will be determined based on the circumstances and communicated 
                  to all affected participants.
                </p>
                <p>
                  Force majeure refunds or credits are subject to verification and cannot be guaranteed for expenses already incurred.
                </p>
              </div>

              <div className="policy-section">
                <h2>6. Partial Cancellations</h2>
                <p>
                  If you need to cancel participation for some members of your group but not others, 
                  the cancellation policy applies to the cancelled participants only. The remaining 
                  participants can continue with the tour as planned.
                </p>
              </div>

              <div className="policy-section">
                <h2>7. No-Show Policy</h2>
                <p>
                  Participants who do not show up on the tour departure date without prior notice 
                  will not be eligible for any refund. This includes participants who arrive late 
                  and miss the tour departure.
                </p>
              </div>

              <div className="policy-section">
                <h2>8. Tour Modifications</h2>
                <p>
                  If we need to modify or cancel a tour due to safety concerns, weather conditions, 
                  or other unforeseen circumstances, we will:
                </p>
                <ul>
                  <li>Provide alternative arrangements when possible</li>
                  <li>Offer a full refund if no suitable alternative is available</li>
                  <li>Communicate changes as early as possible</li>
                </ul>
                <p>
                  Minor timing or route changes shall not be considered grounds for refunds.
                </p>
              </div>

              <div className="policy-section">
                <h2>9. Travel Insurance</h2>
                <p>
                  NomadTrailz does not provide travel insurance but strongly recommends that travelers obtain their own.
                </p>
                <p>
                  We strongly recommend purchasing travel insurance that covers trip cancellation. 
                  This can provide additional protection beyond our cancellation policy, especially 
                  for medical emergencies or other personal circumstances.
                </p>
              </div>

              <div className="policy-section">
                <h2>10. Dispute Resolution</h2>
                <p>
                  If you have concerns about our cancellation policy or refund processing, please 
                  contact us directly. We are committed to resolving any issues fairly and promptly.
                </p>
              </div>

              <div className="policy-footer">
                <p>
                  <strong>Last Updated:</strong> December 2024<br />
                  <strong>Contact:</strong> For cancellation requests or questions, please contact us at {companyInfo.email}
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default CancellationPolicy;
